import {gql} from '@apollo/client';
import {Link, Pane, TickCircleIcon} from 'evergreen-ui';
import React, {ReactNode, useEffect, useReducer} from 'react';
import {Column, EmailLabelWithCopy, EnrichedTable, ErrorDisplay, LoadingSpinner, RelayConnection, SimpleDropdown, SortBy} from '../../../common';
import {formatISODateStr} from '../../../common/util/date';
import {useLazyQuery$} from '../../../graphqlsupport';
import {useObservable} from '../../../rxsupport';
import {SortOrder} from '../../../placement/domains';
import {
    RelayPagination,
    RelayPaginationActionPayload,
    RelayPaginationState,
    reduceRelayPaginationAction,
    useRelayPagination,
} from '../../pagination';
import {RosterUploadInfo} from './domain/types';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
const PORTAL_LINK_BLUE = '#5566FE';
// Upload Status is always "Successful": failed uploads never persist (BHPNPP-3108). Evergreen success green.
const UPLOAD_STATUS_GREEN = '#52BD94';

// FE column attr → BE whitelist sort key (RosterUploadQueryAdapter#resolveSort). A column absent from
// this map is not sortable (e.g. Download Link).
const SORT_KEY_BY_COLUMN: Partial<Record<keyof RosterUploadInfo, string>> = {
    uploadedAt: 'uploadedAt',
    uploadedByFirstName: 'uploadedBy',
    caseNumber: 'caseNumber',
};
const DEFAULT_SORT: SortBy<RosterUploadInfo> = {column: 'uploadedAt', order: SortOrder.DESC};

// Tray-style download glyph — the Upload Roster icon mirrored to point down — matching the mockup's
// Download Link column. Sized to the link text height.
const DownloadTrayIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={PORTAL_LINK_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         style={{marginRight: 6}} aria-hidden="true">
        <path d="M12 4v12"/>
        <path d="M7 11l5 5 5-5"/>
        <path d="M5 14v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4"/>
    </svg>
);

export const ROSTER_UPLOADS_QUERY = gql`
    query rosterUploadsByProvider($first: Int = 10, $after: String, $sortBy: String, $sortDir: String) {
        rosterUploadsByProvider(first: $first, after: $after, sortBy: $sortBy, sortDir: $sortDir) {
            edges {
                cursor
                node {
                    guid
                    uploadedAt
                    uploadedByFirstName
                    uploadedByLastName
                    uploadedByEmail
                    caseNumber
                    downloadUrl
                }
            }
            totalElements
            pageInfo {
                hasPreviousPage
                endCursor
                hasNextPage
                startCursor
            }
        }
    }
`;

type RosterHistoryResponse = RelayConnection<RosterUploadInfo> & {totalElements: number};

type HistoryState = {pagination: RelayPaginationState; pageSize: number; sortBy: SortBy<RosterUploadInfo>};
type HistoryAction =
    | RelayPaginationActionPayload
    | {type: 'SET_PAGE_SIZE'; pageSize: number}
    | {type: 'SET_SORT_BY'; sortBy: SortBy<RosterUploadInfo>};

function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
    switch (action.type) {
        case 'RELAY_PAGINATION_ACTION':
            return {...state, pagination: reduceRelayPaginationAction(state.pagination, action)};
        case 'SET_PAGE_SIZE':
            // Page-size change resets to the first page.
            return {...state, pageSize: action.pageSize, pagination: new RelayPaginationState('0', action.pageSize)};
        case 'SET_SORT_BY':
            // Sort change resets to the first page.
            return {...state, sortBy: action.sortBy, pagination: new RelayPaginationState('0', state.pageSize)};
        default:
            return state;
    }
}

type RosterUploadHistoryTableProps = {
    /** Bumped by the page after a successful upload to force a refetch. */
    refreshToken: number;
    /** Left side of the toolbar — the Download Template link. */
    leftActions?: ReactNode;
    /** Right side of the toolbar, before the pagination controls — the Upload Roster button. */
    rightActions?: ReactNode;
};

export const RosterUploadHistoryTable = ({refreshToken, leftActions, rightActions}: RosterUploadHistoryTableProps) => {
    const [{pagination, pageSize, sortBy}, dispatch] = useReducer(historyReducer, {
        pagination: new RelayPaginationState('0', DEFAULT_PAGE_SIZE),
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy: DEFAULT_SORT,
    });

    const sortVars = () => ({
        sortBy: SORT_KEY_BY_COLUMN[sortBy.column as keyof RosterUploadInfo] ?? 'uploadedAt',
        sortDir: sortBy.order,
    });

    const {execute, data, isRunning$, error} = useLazyQuery$<
        {first: number; after: string; sortBy: string; sortDir: string},
        {rosterUploadsByProvider: RosterHistoryResponse}
    >(ROSTER_UPLOADS_QUERY, {...pagination.toGraphQlParams(), ...sortVars()});

    useEffect(() => {
        execute({...pagination.toGraphQlParams(), ...sortVars()});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, sortBy, refreshToken]);

    const loading = useObservable(isRunning$, true);
    const totalRows = data?.rosterUploadsByProvider?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalRows);
    const rows = data?.rosterUploadsByProvider?.edges.map((edge) => edge.node) ?? [];

    if (error) {
        return <ErrorDisplay error={error} title="Error loading upload history"/>;
    }

    return (
        <Pane className="flex flex-col" flexGrow={1}>
            <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
                <Pane>{leftActions}</Pane>
                <Pane display="flex" alignItems="center">
                    {rightActions}
                    <SimpleDropdown options={PAGE_SIZE_OPTIONS} selected={pageSize.toString()}
                                    minWidth={'2rem'}
                                    textColor={'gray-700'}
                                    setSelected={(newPageSize) => dispatch({type: 'SET_PAGE_SIZE', pageSize: parseInt(newPageSize)})}/>
                    <RelayPagination pageInfo={data?.rosterUploadsByProvider.pageInfo}
                                     count={totalRows}
                                     onClick={onPaginationClick}/>
                </Pane>
            </Pane>
            <Pane borderTop="1px solid #E6E8EC" marginBottom={20}/>
            <EnrichedTable<RosterUploadInfo>
                firstColumn={'uploadedAt'}
                sortBy={sortBy}
                setSortBy={(col: Column<RosterUploadInfo>) => {
                    // Download Link (absent from the map) is not sortable.
                    if (!SORT_KEY_BY_COLUMN[col.attr as keyof RosterUploadInfo]) {
                        return;
                    }
                    // First click on a column sorts desc; clicking the active desc column flips to asc.
                    const order = col.attr === sortBy.column && sortBy.order === SortOrder.DESC
                        ? SortOrder.ASC
                        : SortOrder.DESC;
                    dispatch({type: 'SET_SORT_BY', sortBy: {column: col.attr, order}});
                }}
                styleOverride={{
                    trHeader: 'portal-history-header text-left select-none text-sm',
                }}
                columns={[
                    {attr: 'uploadedAt', name: 'Date/Time of Upload', span: 3},
                    {attr: 'uploadedByFirstName', name: 'Uploaded By', span: 3},
                    {attr: 'caseNumber', name: 'Case Number', span: 2},
                    {attr: 'uploadStatus', name: 'Upload Status', span: 2},
                    {attr: 'downloadUrl', name: 'Download Link', span: 2},
                ]}
                rowsData={rows}
                keyColumn={'guid'}
                cellDisplayFunc={(row, column) => {
                    if (column.attr === 'uploadedAt') {
                        return formatISODateStr(row.uploadedAt, 'MM/dd/yyyy hh:mm a');
                    }
                    if (column.attr === 'uploadedByFirstName') {
                        const name = `${row.uploadedByFirstName ?? ''} ${row.uploadedByLastName ?? ''}`.trim();
                        // Raw <a> inherits the 16px row-data size (evergreen Link forced 14px) and adds
                        // the hover-email + copy affordance (BHPNPP-3108).
                        return <EmailLabelWithCopy email={row.uploadedByEmail} label={name}/>;
                    }
                    if (column.attr === 'caseNumber') {
                        return row.caseNumber ?? 'N/A';
                    }
                    if (column.attr === 'uploadStatus') {
                        return (
                            <span className="inline-flex items-center" style={{color: UPLOAD_STATUS_GREEN}}>
                                <TickCircleIcon size={14} color={UPLOAD_STATUS_GREEN} marginRight={6}/>
                                Successful
                            </span>
                        );
                    }
                    if (column.attr === 'downloadUrl') {
                        return row.downloadUrl
                            ? <Link href={row.downloadUrl} target="_blank" rel="noopener" size={500}
                                    display="inline-flex" alignItems="center">
                                  <DownloadTrayIcon/>
                                  <span className="text-blue-portal-link">Download</span>
                              </Link>
                            : 'N/A';
                    }
                    return '';
                }}
                emptyMessage={
                    <div className="w-full flex items-center">
                        <div className="m-auto pt-8">No roster uploads yet</div>
                    </div>
                }
                loadingFeat={{
                    loading,
                    loadingDisplayElement: <LoadingSpinner spacing={20} height={'30vh'}/>,
                }}
            />
        </Pane>
    );
};
